/**
 * <quick-add-dialog> lets shoppers pick a variant and add it to the cart without leaving the page
 * (see snippets/quick-add-dialog.liquid).
 *
 * Triggers can live anywhere on the page. Clicks are delegated, so triggers in re-rendered markup
 * like the cart drawer keep working:
 *   <button type="button" data-quick-add="/products/handle" aria-haspopup="dialog">
 *
 * On click it fetches the product page and lifts the media column, title, info badges, description and buy box out of the
 * product section's `[data-product-grid]`, the way Horizon's quick add does. Inside the dialog:
 * - <product-variant-picker> keeps re-rendering the product section; `data-variant-scope` limits
 *   its swaps to the dialog, and the page URL is left alone.
 * - <add-to-cart> adds as on the product page, and the dialog closes once the add succeeds.
 *
 * The trigger gets `aria-busy="true"` and the Dawn `loading` class while the product loads. If it
 * can't load, the shopper is sent to the product page instead.
 */
const KEEP_BLOCKS = [
  ".product-title",
  '[data-variant-update^="info-badges-"]',
  ".richtext",
  ".product-buy-box",
].join(", ");
const REMOVE = [
  ".product__additional-content",
  ".product-media__lightbox",
  ".product-shipping-info",
].join(", ");

class QuickAddDialog extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    this.content = this.querySelector("[data-quick-add-content]");
    this.detailsTemplate = this.querySelector("[data-quick-add-details]");

    document.addEventListener("click", this.#onClick);
    this.dialog.addEventListener("shopify:cart:lines-update", this.#onCartUpdate);
    this.dialog.addEventListener("close", this.#onClose);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.#onClick);
    this.#abortController?.abort();
  }

  #onClick = (event) => {
    const trigger = event.target.closest("[data-quick-add]");
    if (!trigger) return;

    event.preventDefault();
    this.open(trigger);
  };

  async open(trigger) {
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    const url = trigger.dataset.quickAdd;

    this.#setLoading(trigger, true);

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok)
        throw new Error(`Product request failed with status ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), "text/html");
      const grid = html.querySelector("[data-product-grid]");
      if (!grid) throw new Error(`No [data-product-grid] on ${url}`);

      const fragment = this.#extract(grid, url);
      await this.#loadStylesheets([...html.head.querySelectorAll('link[rel="stylesheet"]'), ...fragment.querySelectorAll('link[rel="stylesheet"]')]);
      if (controller.signal.aborted) return;

      this.content.replaceChildren(fragment);
      this.#runScripts();
      this.#label();

      if (!this.dialog.open) this.dialog.showModal();
      this.content.scrollTop = 0;
    } catch (error) {
      if (error.name === "AbortError") return;

      console.error(error);
      window.location.href = url;
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
      this.#setLoading(trigger, false);
    }
  }

  /**
   * The dialog's content: the media column and the title, info badges, description and buy box blocks.
   */
  #extract(grid, url) {
    grid.querySelectorAll(REMOVE).forEach((element) => element.remove());

    const content = grid.querySelector(".product-content");
    [...(content?.children ?? [])].forEach((block) => {
      if (!block.matches(KEEP_BLOCKS)) block.remove();
    });

    if (content && this.detailsTemplate) {
      const details = this.detailsTemplate.content.cloneNode(true);
      details.querySelector("a").href = url;
      content.append(details);
    }

    // The desktop grid layout stacks every image; a slider fits the dialog
    grid.querySelector(".product-media-column--grid")?.classList.remove("product-media-column--grid");
    grid.querySelectorAll("product-media").forEach((media) => {
      media.removeAttribute("data-desktop-grid");
      media.classList.remove("product-media--grid");
    });

    // Picking a variant here shouldn't change the page URL
    grid.querySelectorAll("product-variant-picker").forEach((picker) => picker.removeAttribute("data-update-url"));

    const fragment = document.createDocumentFragment();
    fragment.append(...grid.children);
    return fragment;
  }

  /**
   * Adds the product page's stylesheets this page doesn't load yet, resolving once they're applied.
   */
  #loadStylesheets(links) {
    const loaded = new Set(
      [...document.querySelectorAll('link[rel="stylesheet"]')].map((link) => link.href),
    );

    const pending = [];
    for (const link of links) {
      const href = new URL(link.getAttribute("href"), window.location.href).href;
      if (loaded.has(href)) continue;
      loaded.add(href);

      const stylesheet = document.createElement("link");
      stylesheet.rel = "stylesheet";
      stylesheet.href = href;
      pending.push(
        new Promise((resolve) => {
          stylesheet.onload = stylesheet.onerror = resolve;
        }),
      );
      document.head.append(stylesheet);
    }

    return Promise.all(pending);
  }

  /**
   * Parsed scripts don't run when inserted, so they're recreated. Modules already loaded on the
   * page don't run twice.
   */
  #runScripts() {
    for (const parsed of this.content.querySelectorAll("script")) {
      if (parsed.type && parsed.type !== "module" && parsed.type !== "text/javascript") continue;

      const script = document.createElement("script");
      for (const { name, value } of parsed.attributes) script.setAttribute(name, value);
      script.textContent = parsed.textContent;
      parsed.replaceWith(script);
    }
  }

  /**
   * Names the dialog after the product title.
   */
  #label() {
    const heading = this.content.querySelector(".product-title__heading");
    if (!heading) {
      this.dialog.removeAttribute("aria-labelledby");
      return;
    }

    heading.id = `${this.dialog.id}-title`;
    this.dialog.setAttribute("aria-labelledby", heading.id);
  }

  #setLoading(trigger, loading) {
    trigger.classList.toggle("loading", loading);
    trigger.toggleAttribute("aria-busy", loading);
    trigger.querySelector(".loading__spinner")?.classList.toggle("hidden", !loading);
  }

  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.didError) this.dialog.close();
      })
      // Failed requests are reported through `shopify:cart:error`
      .catch(() => {});
  };

  #onClose = () => {
    this.#abortController?.abort();

    // Emptied once the closing fade ends, so the content doesn't vanish mid-animation and no
    // duplicate ids (forms, blocks) stay behind on the product page
    Promise.allSettled(this.dialog.getAnimations().map((animation) => animation.finished)).then(() => {
      if (!this.dialog.open) this.content.replaceChildren();
    });
  };
}

if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
